/**
 * Citations and the no-source mark under a chat answer (reliability plan 3.5).
 *
 * The decisions live in the engine (bunnyquery/engine citations.ts): which
 * bubbles qualify (retrievalNoteOfAnswer), what the chips are (citationsFor,
 * citationLinkPart) and what the mark says (NO_DATA_TOOL_MARK). This module
 * binds them to what agent.vue has locally, its cache of already-minted urls,
 * and returns parts the template draws with the SAME anchor it draws an inline
 * stored-file link with, so a chip takes onMessageLinkClick, the unavailable
 * state and the fetching state unchanged. DOM-free so it can be tested on its
 * own (tests/chat-citations.test.mjs).
 */
import {
    citationsFor,
    citationLinkPart,
    answerUsedNoDataTool,
    retrievalNoteOfAnswer,
    type CitationLinkPart,
    type RetrievalNote,
} from "bunnyquery/engine";

/** The slice of a bubble's message the gate reads. */
export type CitationBubbleMessage = {
    role?: string;
    isError?: boolean;
    isPending?: boolean;
    isBackgroundTask?: boolean;
    _streamPending?: boolean;
    _retrieval?: RetrievalNote | null;
};

/**
 * The citation chips for one bubble, or [] when it has none (not an answer, no
 * note, nothing cited). `resolveFreshHref` is the view's minted-url cache lookup
 * keyed by the `_expired_.url` placeholder, exactly what classifyInlineLink gets.
 */
export function citationPartsFor(
    msg: CitationBubbleMessage | null | undefined,
    resolveFreshHref?: (expiredHref: string) => string | undefined,
): CitationLinkPart[] {
    const note = retrievalNoteOfAnswer(msg);
    if (!note) return [];
    return citationsFor(note).map((c) => citationLinkPart(c, resolveFreshHref));
}

/** True when the bubble is a chat answer whose turn read no project data. */
export function showsNoSourceMark(msg: CitationBubbleMessage | null | undefined): boolean {
    return answerUsedNoDataTool(retrievalNoteOfAnswer(msg));
}
